import React, { useState } from 'react';
import { PolicyRevision, DiffMode, Company } from '../../types';
import { useDiffViewer } from '../../hooks/useDiffViewer';
import { SplitDiffView } from './SplitDiffView';
import { UnifiedDiffView } from './UnifiedDiffView';
import { AiSummaryCard } from './AiSummaryCard';
import { DiffToolbar } from './DiffToolbar';
import { ArrowLeft, Columns, AlignJustify, ExternalLink, BookOpen } from 'lucide-react';

interface DiffViewerProps {
  companyId: string;
  policyType: string;
  baseRevisionId: string;
  targetRevisionId: string;
  revisions: PolicyRevision[];
  onClose: () => void;
  company?: Company;
  onReadDocument?: (revision: PolicyRevision) => void;
}

export const DiffViewer: React.FC<DiffViewerProps> = ({
  companyId,
  policyType,
  baseRevisionId,
  targetRevisionId,
  revisions,
  onClose,
  company,
  onReadDocument,
}) => {
  const [diffMode, setDiffMode] = useState<DiffMode>('split');
  const [foldUnchanged, setFoldUnchanged] = useState<boolean>(true);
  const [ignoreWhitespace, setIgnoreWhitespace] = useState<boolean>(false);
  const [activeHunkIndex, setActiveHunkIndex] = useState<number>(0);

  const baseRev = revisions.find((r) => r.id === baseRevisionId);
  const targetRev = revisions.find((r) => r.id === targetRevisionId);

  const trackedUrl =
    company?.trackedPolicies?.[policyType]?.url ||
    company?.tracked_policies?.[policyType]?.url;

  const documentUrl =
    targetRev?.url ||
    targetRev?.sourceUrl ||
    targetRev?.source_url ||
    baseRev?.url ||
    baseRev?.sourceUrl ||
    baseRev?.source_url ||
    trackedUrl;

  const { diffResult } = useDiffViewer({
    baseRev,
    targetRev,
    ignoreWhitespace,
  });

  const totalHunks = diffResult?.hunks?.length || 0;

  const handleNextHunk = () => {
    if (totalHunks > 0) {
      setActiveHunkIndex((prev) => (prev + 1) % totalHunks);
    }
  };

  const handlePrevHunk = () => {
    if (totalHunks > 0) {
      setActiveHunkIndex((prev) => (prev - 1 + totalHunks) % totalHunks);
    }
  };

  const analysis = targetRev?.diffAnalysis || targetRev?.summary || diffResult?.summary;

  return (
    <div className="space-y-5">
      {/* Diff Header */}
      <div className="bg-white dark:bg-slate-800 p-4 sm:p-5 rounded-xl border border-slate-200 dark:border-slate-700 shadow-2xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition-colors"
            title="Return to Timeline"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Comparing Revisions
              </h2>
              <span className="text-xs font-semibold px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300">
                {policyType.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mt-1">
              <span>Base:</span>
              {onReadDocument && baseRev ? (
                <button
                  onClick={() => onReadDocument(baseRev)}
                  className="font-mono text-slate-700 dark:text-slate-300 font-semibold hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline cursor-pointer"
                  title="Read base revision in document reader"
                >
                  {baseRev.title || baseRevisionId}
                </button>
              ) : (
                <span className="font-mono text-slate-700 dark:text-slate-300 font-semibold">
                  {baseRev?.title || baseRevisionId}
                </span>
              )}
              <span>→</span>
              <span>Target:</span>
              {onReadDocument && targetRev ? (
                <button
                  onClick={() => onReadDocument(targetRev)}
                  className="font-mono text-slate-700 dark:text-slate-300 font-semibold hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline cursor-pointer"
                  title="Read target revision in document reader"
                >
                  {targetRev.title || targetRevisionId}
                </button>
              ) : (
                <span className="font-mono text-slate-700 dark:text-slate-300 font-semibold">
                  {targetRev?.title || targetRevisionId}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons & View Mode Switcher */}
        <div className="flex flex-wrap items-center gap-2">
          {onReadDocument && targetRev && (
            <button
              onClick={() => onReadDocument(targetRev)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 border border-indigo-200 dark:border-indigo-800 transition-colors cursor-pointer shadow-2xs"
              title="Open full document in reader"
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span>Read Document</span>
            </button>
          )}

          {documentUrl && (
            <a
              href={documentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 transition-colors shadow-2xs"
              title="Open original live document in new tab"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span>Original URL</span>
            </a>
          )}

          {/* View Mode Switcher (Split vs Unified) */}
          <div className="flex bg-slate-100 dark:bg-slate-700 p-1 rounded-lg">
            <button
              onClick={() => setDiffMode('split')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                diffMode === 'split'
                  ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-2xs'
                  : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <Columns className="w-3.5 h-3.5" />
              Side-by-Side
            </button>
            <button
              onClick={() => setDiffMode('unified')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                diffMode === 'unified'
                  ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-2xs'
                  : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <AlignJustify className="w-3.5 h-3.5" />
              Unified Stream
            </button>
          </div>
        </div>
      </div>

      {/* AI Executive Summary Card */}
      {analysis && (
        <AiSummaryCard
          analysis={analysis}
          onReadDocument={onReadDocument && targetRev ? () => onReadDocument(targetRev) : undefined}
          documentUrl={documentUrl}
        />
      )}

      {/* Diff Toolbar (Jumping, folding, export) */}
      <DiffToolbar
        totalHunks={totalHunks}
        activeHunkIndex={activeHunkIndex}
        onNextHunk={handleNextHunk}
        onPrevHunk={handlePrevHunk}
        foldUnchanged={foldUnchanged}
        onToggleFold={() => setFoldUnchanged(!foldUnchanged)}
        ignoreWhitespace={ignoreWhitespace}
        onToggleWhitespace={() => setIgnoreWhitespace(!ignoreWhitespace)}
        stats={diffResult?.stats}
      />

      {/* Split or Unified View Container */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700/80 overflow-hidden shadow-2xs">
        {diffMode === 'split' ? (
          <SplitDiffView
            hunks={diffResult?.hunks || []}
            foldUnchanged={foldUnchanged}
            activeHunkIndex={activeHunkIndex}
          />
        ) : (
          <UnifiedDiffView
            hunks={diffResult?.hunks || []}
            foldUnchanged={foldUnchanged}
            activeHunkIndex={activeHunkIndex}
          />
        )}
      </div>
    </div>
  );
};
